import type { MenuElement } from "prosemirror-menu";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { createPencilEditSvg, createPlusSvg } from "../icons";
import { key as characterCountKey } from "../plugins/character-count";
import { toolbarPluginKey } from "../plugins/toolbar";

import { t } from "i18n/i18n";

function getCharacterCountState(state: EditorState) {
  return characterCountKey.getState(state) ?? { limit: null, display: "NONE" as const };
}

function setLimit(view: EditorView, value: string): void {
  const parsedLimit = Number.parseInt(value, 10);
  const limit = value === "" || parsedLimit <= 0 ? null : parsedLimit;
  if (limit === getCharacterCountState(view.state).limit) return;
  view.dispatch(view.state.tr.setMeta(characterCountKey, { limit }));
}

export function createCharacterLimitPanelItem(): MenuElement {
  return {
    render(view: EditorView) {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "character-limit-panel-item flex items-center gap-1 px-2 whitespace-nowrap";

      const icon = document.createElement("span");
      icon.className = "size-4";

      const label = document.createElement("span");
      button.append(icon, label);

      button.addEventListener("click", (event) => {
        const isKeyboard = event.detail === 0; // detail = click count
        view.focus(); // keeps :focus-within alive during dispatch; no-op for mouse
        view.dispatch(
          view.state.tr.setMeta(toolbarPluginKey, {
            activePanelId: "characterLimit",
            shouldFocus: isKeyboard,
          }),
        );
      });

      return {
        dom: button,
        update: (state: EditorState) => {
          const { limit, display } = getCharacterCountState(state);
          if (display !== "EDIT") return false;

          icon.replaceChildren(limit === null ? createPlusSvg() : createPencilEditSvg());
          const text =
            limit === null
              ? t("character_counter.add_limit")
              : t("character_counter.limit_chars", { maxLength: limit });
          label.textContent = text;
          button.title = t("character_counter.character_limit");
          button.ariaLabel = text;
          return true;
        },
      };
    },
  };
}

export function createCharacterLimitItem(): MenuElement {
  return {
    render(view: EditorView) {
      const form = document.createElement("form");
      form.className = "character-limit-item flex h-7 items-center gap-2 px-2 text-sm";

      const label = document.createElement("label");
      label.textContent = t("character_counter.add_character_limit");

      const input = document.createElement("input");
      input.type = "number";
      input.name = "character-limit";
      input.placeholder = "300";
      input.className =
        "w-[64px] rounded border border-black-tint-90 bg-white px-2 py-1 text-black-tint-20";
      input.setAttribute("aria-label", label.textContent);
      input.addEventListener("change", () => setLimit(view, input.value));
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopPropagation();
        setLimit(view, input.value);
        view.focus();
        view.dispatch(
          view.state.tr.setMeta(toolbarPluginKey, {
            activePanelId: "main",
            shouldFocus: true,
          }),
        );
      });
      form.append(label, input);

      return {
        dom: form,
        focusable: input,
        update: (state: EditorState) => {
          const { limit } = getCharacterCountState(state);
          const nextValue = limit === null ? "" : String(limit);
          if (input.value !== nextValue) input.value = nextValue;
          return true;
        },
      };
    },
  };
}
